import React, { useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Hand, PenTool, RefreshCw, Undo2 } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { DEPT_NAMES } from '@/lib/constants';
import { PRIORITY_MAP } from '@/lib/status-colors';
import {
  ActionRow, Card, EmptyState, PageHeader, Pager, PhonePage, PillButton, ScreenLoader, SearchBar, StatusPill, TabRow,
} from '@/components/phone/primitives';
import { Colors, priorityTone } from '@/components/phone/theme';

/**
 * The designer's workspace, on a phone.
 *
 * On a desktop the work and the pile sit side by side, because deciding
 * whether to pull more is the moment you need both. A phone cannot show both,
 * so they are tabs — my work, what is available to take, what was finished —
 * each a list of cards with the row's actions as thumb-sized buttons. The page
 * keeps every handler; this only draws them.
 */

const AVAILABLE_PAGE_SIZE = 30;

const DESIGN_STATUS = {
  pending_assignment: ['Pending', 'neutral'],
  assigned: ['Assigned', 'accent'],
  in_progress: ['In progress', 'caution'],
  completed: ['Completed', 'positive'],
  not_required: ['N/A', 'neutral'],
};

const isDone = (item) => item.design_status === 'completed' || item.design_status === 'not_required';

function OrderCard({ order, openCount, expanded, onToggle, onOpen, onCompleteAll, onCompleteItem, onRelease, completing, releasing }) {
  const items = order.line_items || [];
  return (
    <Card className="mt-3" testID={`my-order-${order.order_id}`}>
      <div className="min-w-0">
        <p className="line-clamp-2 text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
          {items[0]?.item_name || order.customer_name}
          {items.length > 1 && <span className="font-medium" style={{ color: Colors.text.tertiary }}> +{items.length - 1} more</span>}
        </p>
        <p className="mt-0.5 truncate text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
          {order.customer_name}{order.firm_name ? ` — ${order.firm_name}` : ''}
        </p>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]" style={{ color: Colors.text.secondary }}>
        <span className="font-medium tabular-nums">{order.order_id}</span>
        {!!order.priority && <StatusPill label={PRIORITY_MAP[order.priority]?.label || order.priority} tone={priorityTone(order.priority)} />}
        <span>Due {order.expected_delivery_date ? formatIST(order.expected_delivery_date, { dateOnly: true }) : '—'}</span>
        {openCount > 0 && <span className="tabular-nums">{openCount} open</span>}
      </div>

      <ActionRow
        actions={[
          openCount > 0 && {
            key: 'all', label: 'All done', icon: CheckCircle2, tone: 'positive', disabled: completing === order.id,
            testID: `complete-all-${order.order_id}`, onClick: () => onCompleteAll(order.id),
          },
          { key: 'open', label: 'Open order', icon: ExternalLink, tone: 'accent', testID: `view-order-${order.order_id}`, onClick: () => onOpen(order) },
          items.length > 0 && {
            key: 'items', label: expanded ? 'Hide items' : `${items.length} item${items.length === 1 ? '' : 's'}`,
            icon: expanded ? ChevronDown : ChevronRight, testID: `expand-order-${order.order_id}`, onClick: onToggle,
          },
        ]}
      />

      {expanded && (
        <ul className="border-t" style={{ borderColor: Colors.border.subtle }}>
          {items.map(item => {
            const [label, tone] = DESIGN_STATUS[item.design_status] || DESIGN_STATUS.pending_assignment;
            const key = `${order.id}-${item.id}`;
            return (
              <li key={item.id} className="border-b py-2.5 last:border-b-0" style={{ borderColor: Colors.border.subtle }} data-testid={`my-item-${item.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-medium" style={{ color: Colors.text.primary }}>
                      {item.item_name || item.product_subtype || DEPT_NAMES[item.order_type] || 'Item'}
                    </p>
                    <p className="mt-0.5 text-[12px]" style={{ color: Colors.text.secondary }}>Qty {item.quantity?.toLocaleString()}</p>
                  </div>
                  <StatusPill label={label} tone={tone} />
                </div>
                {!isDone(item) && (
                  <ActionRow
                    actions={[
                      { key: 'done', label: 'Mark done', icon: CheckCircle2, tone: 'positive', disabled: completing === key,
                        testID: `complete-item-${item.id}`, onClick: () => onCompleteItem(order.id, item.id) },
                      { key: 'release', label: 'Give back', icon: Undo2, disabled: releasing === key,
                        testID: `release-item-${item.id}`, onClick: () => onRelease(order.id, item.id) },
                    ]}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export default function PhoneMyDesigns({
  user, orders, recentlyCompleted, openCountOf, setSearchQuery,
  expandedOrder, toggleOrder, completing, releasing, claiming,
  available, availableTotal, availableLoading, availablePage, setAvailablePage, setAvailableSearch,
  onCompleteAll, onCompleteItem, onRelease, onClaimOrder, onOpenOrder, onRefresh,
}) {
  const [tab, setTab] = useState('mine');
  const openItems = orders.reduce((sum, o) => sum + openCountOf(o), 0);
  const tabs = [
    { key: 'mine', label: `My work · ${orders.length}`, testID: 'tab-mine' },
    { key: 'available', label: `Available · ${availableTotal || available.length}`, testID: 'tab-available' },
    { key: 'done', label: `Done · ${recentlyCompleted.length}`, testID: 'tab-done' },
  ];

  return (
    <PhonePage testID="my-designs-page">
      <PageHeader
        title="Designer Workspace"
        subtitle={`${user?.full_name || ''}${openItems ? ` · ${openItems} item${openItems === 1 ? '' : 's'} open` : ''}`}
        action={<PillButton onClick={onRefresh} testID="refresh-btn"><RefreshCw className="h-4 w-4" /> Refresh</PillButton>}
      />

      <div className="flex-1 px-4 pb-10">
        <TabRow tabs={tabs} active={tab} onSelect={setTab} />

        {tab === 'mine' && (
          <>
            <div className="mt-3"><SearchBar placeholder="Order, customer…" onChange={setSearchQuery} /></div>
            {orders.length === 0 ? (
              <EmptyState icon={PenTool} message="Nothing assigned to you" hint="Take something from the Available tab." />
            ) : orders.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                openCount={openCountOf(order)}
                expanded={expandedOrder === order.id}
                onToggle={() => toggleOrder(order.id)}
                onOpen={onOpenOrder}
                onCompleteAll={onCompleteAll}
                onCompleteItem={onCompleteItem}
                onRelease={onRelease}
                completing={completing}
                releasing={releasing}
              />
            ))}
          </>
        )}

        {tab === 'available' && (
          <>
            <div className="mt-3">
              <SearchBar placeholder="Find an order to take…" testID="available-search" onChange={(q) => { setAvailableSearch(q); setAvailablePage(1); }} />
            </div>
            {availableLoading ? <ScreenLoader /> : available.length === 0 ? (
              <EmptyState icon={Hand} message="Nothing waiting to be picked up" />
            ) : available.map(order => (
              <Card key={order.id} className="mt-3" testID={`available-${order.order_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-[15px] font-bold" style={{ color: Colors.text.primary }}>
                      {order.line_items?.[0]?.item_name || order.customer_name}
                    </p>
                    <p className="mt-0.5 truncate text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
                      {order.order_id}{order.firm_name ? ` · ${order.firm_name}` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    title="Take"
                    disabled={claiming === order.id}
                    onClick={() => onClaimOrder(order.id)}
                    data-testid={`claim-${order.order_id}`}
                    className="flex h-11 shrink-0 items-center gap-1.5 rounded-[10px] px-4 text-[13px] font-semibold disabled:opacity-60"
                    style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
                  >
                    <Hand className="h-4 w-4" /> Take
                  </button>
                </div>
              </Card>
            ))}
            <Pager page={availablePage} totalPages={Math.ceil((availableTotal || 0) / AVAILABLE_PAGE_SIZE)} onPage={setAvailablePage} />
          </>
        )}

        {tab === 'done' && (
          recentlyCompleted.length === 0 ? (
            <EmptyState icon={CheckCircle2} message="Nothing finished recently" />
          ) : (
            <Card className="mt-3" title="Recently completed">
              {recentlyCompleted.slice(0, 12).map(order => (
                <button
                  key={order.id}
                  type="button"
                  onClick={() => onOpenOrder(order)}
                  className="flex w-full items-center justify-between gap-2 border-b py-3 text-left last:border-b-0"
                  style={{ borderColor: Colors.border.subtle }}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-medium" style={{ color: Colors.text.primary }}>{order.customer_name}</span>
                    <span className="block truncate text-[12px]" style={{ color: Colors.text.tertiary }}>{order.order_id}</span>
                  </span>
                  <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: Colors.semantic.positive }} />
                </button>
              ))}
            </Card>
          )
        )}
      </div>
    </PhonePage>
  );
}
